import React, { useState, useEffect, useCallback, useMemo } from 'react';
import UnprocessedItemsButton from './UnprocessedItemsButton';
import UnprocessedItemsModal from './UnprocessedItemsModal';
import ClientDocumentCountService from '../../Services/ClientDocumentCount.service';

/** Format date as reporting-month key "MM/yy" (matches backend ToString("MM/yy")). Uses UTC to match Date.UTC storage. */
function toReportingMonthKey(date) {
  if (!date) return null;
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const yy = String(d.getUTCFullYear()).slice(-2);
  return `${mm}/${yy}`;
}

/** Inclusive list of MM/yy keys from start month through end month. */
function monthsInRange(start, end) {
  if (!start || !end) return [];
  const from = new Date(start instanceof Date ? start : new Date(start));
  const to = new Date(end instanceof Date ? end : new Date(end));
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return [];
  from.setUTCDate(1);
  from.setUTCHours(12, 0, 0, 0);
  to.setUTCDate(1);
  to.setUTCHours(12, 0, 0, 0);
  if (from > to) return [toReportingMonthKey(to)].filter(Boolean);

  const keys = [];
  const cursor = new Date(from);
  while (cursor <= to) {
    keys.push(toReportingMonthKey(cursor));
    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }
  return keys.filter(Boolean);
}

function rowTotal(row) {
  if (row == null) return 0;
  if (row.total != null || row.Total != null) return Number(row.total ?? row.Total) || 0;
  return (
    (Number(row.incomes ?? row.Incomes) || 0) +
    (Number(row.expenses ?? row.Expenses) || 0) +
    (Number(row.journalEntries ?? row.JournalEntries) || 0) +
    (Number(row.checks ?? row.Checks) || 0)
  );
}

/**
 * External component for unprocessed documents.
 * Use on ClientDetails, Document, JournalEntry, PaymentAcceptance (or any screen with client + token).
 * Shows a pulsed red button when client has unprocessed items; click opens modal with summary by reporting month and table; row click opens EditModal (global).
 *
 * @param {Date} [selectedDate] – primary selected month (ClientDetails `date` / Document `sortDate`)
 * @param {Date} [startDate] – range / bi-monthly start (optional)
 * @param {Date} [endDate] – range / bi-monthly end (optional)
 * @param {boolean} [isRange] – when true, dateCount sums months from startDate..endDate
 */
export default function UnprocessedItems({
  client,
  token,
  disabled,
  selectedDate,
  startDate,
  endDate,
  isRange = false,
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [summaryByMonth, setSummaryByMonth] = useState([]);
  const totalCount = client?.totalCounts ?? 0;

  const fetchSummary = useCallback(async () => {
    const caseId = client?.caseId;
    if (!caseId || !token) {
      setSummaryByMonth([]);
      return;
    }
    try {
      const data = await ClientDocumentCountService.getUnprocessedSummaryByMonth(caseId, token);
      setSummaryByMonth(data.summaryByReportingMonth ?? data.SummaryByReportingMonth ?? []);
    } catch (err) {
      console.error('UnprocessedItems summary fetch error:', err);
      setSummaryByMonth([]);
    }
  }, [client?.caseId, token]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary, totalCount]);

  const { dateCount, dateLabel } = useMemo(() => {
    const byKey = new Map();
    (summaryByMonth || []).forEach((row) => {
      const key = row.reportingMonth ?? row.ReportingMonth;
      if (key) byKey.set(key, rowTotal(row));
    });

    if (isRange && startDate && endDate) {
      const keys = monthsInRange(startDate, endDate);
      const sum = keys.reduce((acc, k) => acc + (byKey.get(k) || 0), 0);
      const label =
        keys.length === 1
          ? keys[0]
          : keys.length > 1
            ? `${keys[0]}–${keys[keys.length - 1]}`
            : null;
      return { dateCount: sum, dateLabel: label };
    }

    const key = toReportingMonthKey(selectedDate || startDate);
    if (!key) return { dateCount: null, dateLabel: null };
    return { dateCount: byKey.get(key) || 0, dateLabel: key };
  }, [summaryByMonth, selectedDate, startDate, endDate, isRange]);

  return (
    <>
      <UnprocessedItemsButton
        totalCount={totalCount}
        dateCount={dateCount}
        dateLabel={dateLabel}
        onClick={() => setModalOpen(true)}
        disabled={disabled}
      />
      <UnprocessedItemsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        client={client}
        caseId={client?.caseId}
        token={token}
      />
    </>
  );
}

export { UnprocessedItemsButton, UnprocessedItemsModal };
